//import liraries
import React, { useMemo, useCallback, useState } from 'react';
import { Box, Typography, Card, CardMedia, CardContent, Chip, Stack, IconButton } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { useNavigate } from 'react-router-dom';
import { paiseToRupees } from '../../Utils/currencyConverter';
import ShareModal from '../../Components/ShareModal';

// Memoized TripCard component to prevent unnecessary re-renders
const TripCard = React.memo<{
  trip: any;
  navigate: (path: string) => void;
  onShare: (trip: any) => void;
}>(({ trip, navigate, onShare }) => {
  // Memoize computed values to avoid recalculations on every render
  const imageUrl = useMemo(() => trip?.files?.[0]?.fileURL || '', [trip?.files?.[0]?.fileURL]);
  const duration = useMemo(() => `${trip?.days} Days`, [trip?.days]);
  const price = useMemo(() => {
    return trip?.pricePerPerson
      ? `₹${paiseToRupees(trip.pricePerPerson).toFixed(2)}/person`
      : trip?.price || '₹0.00/person';
  }, [trip?.pricePerPerson, trip?.price]);
  const creator = useMemo(() => trip?.user?.firstName || trip?.createdBy || 'Creator', [trip?.userId?.firstName, trip?.createdBy]);
  const tripType = useMemo(() => trip?.type, [trip?.type]);
  const tripTitle = useMemo(() => trip?.title, [trip?.title]);
  const tripId = useMemo(() => trip?._id, [trip?._id]);

  const handleClick = useCallback(() => {
    if (tripType === 'trip') {
      navigate(`/trips/${tripId}`);
    } else {
      navigate(`/trips/${tripId}?type=itinerary`);
    }
  }, [tripType, tripId, navigate]);

  const handleShare = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onShare(trip);
  }, [trip, onShare]);

  return (
    <Box
      key={tripId}
      sx={{
        flex: '0 0 65%',
        minWidth: 200,
        borderRadius: '24px',
      }}
      onClick={handleClick}
    >
      {/* Image section with overlays */}
      <Box sx={{ position: 'relative' }}>
        <CardMedia sx={{ borderRadius: '16px' }} component="img" height="300" image={imageUrl} alt={tripTitle} />

        {/* Type label */}
        {tripType && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bgcolor: '#FFE0B2',
              color: '#8B4513',
              borderRadius: '0 16px 0 16px',
              px: 2.5,
              py: 0.5,
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {tripType ? `${tripType.charAt(0).toUpperCase()}${tripType.slice(1)}` : ''}
          </Box>
        )}

        {/* Bottom overlays (creator + share) */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
          }}
        >
          <Chip
            label={creator}
            size="small"
            sx={{
              bgcolor: 'rgba(236, 224, 218, 0.6)', // Muted beige-grey with transparency
              backdropFilter: 'blur(4px)',
              fontSize:'14px',
              fontWeight: 500,
              color: '#49454F', // Dark grey text
              borderRadius: '8px', // Rounded rectangular shape
              border: '1px solid rgb(218, 213, 223)',
              padding:'16px',
              '& .MuiChip-label': {
                padding: '16px 8px',
              }
            }}
          />
          <IconButton
            size="small"
            sx={{
              bgcolor: 'rgba(236, 224, 218, 0.6)',
              backdropFilter: 'blur(4px)',
              borderRadius: '8px', // Slightly more rounded for the rounded square look
              border: '1px solid rgb(218, 213, 223)',

              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.85)',
              }
            }}
            onClick={handleShare}
          >
            <ShareIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Details */}
      <Box sx={{ py: 1, px: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {tripTitle}
        </Typography>
        <Typography variant="body2" color="#008387">
          {duration} • {price}
        </Typography>
      </Box>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if trip ID changes or if any relevant data changes
  // Return true if props are equal (skip re-render), false if different (re-render)
  if (prevProps.trip?._id !== nextProps.trip?._id) return false;
  if (prevProps.trip?.title !== nextProps.trip?.title) return false;
  if (prevProps.trip?.days !== nextProps.trip?.days) return false;
  if (prevProps.trip?.pricePerPerson !== nextProps.trip?.pricePerPerson) return false;
  if (prevProps.trip?.price !== nextProps.trip?.price) return false;
  if (prevProps.trip?.type !== nextProps.trip?.type) return false;
  if (prevProps.trip?.files?.[0]?.fileURL !== nextProps.trip?.files?.[0]?.fileURL) return false;
  if (prevProps.trip?.userId?.firstName !== nextProps.trip?.userId?.firstName) return false;
  if (prevProps.trip?.createdBy !== nextProps.trip?.createdBy) return false;
  // navigate should be stable, but check anyway
  if (prevProps.navigate !== nextProps.navigate) return false;
  // onShare should be stable, but check anyway
  if (prevProps.onShare !== nextProps.onShare) return false;
  // All props are equal, skip re-render
  return true;
});

TripCard.displayName = 'TripCard';

// create a component
const DashboardTripScreen: React.FC = () => {
  const navigate = useNavigate();
  // Get trips from dashboard slice
  const trips = useSelector((state: RootState) => state.dashboard.tripsItinerary);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<any>(null);
  
  const handleHeaderClick = useCallback(() => {
    navigate('/trips');
  }, [navigate]);

  const handleShare = useCallback((trip: any) => {
    setSelectedTrip(trip);
    setShareModalOpen(true);
  }, []);

  const handleShareModalClose = useCallback(() => {
    setShareModalOpen(false);
    setSelectedTrip(null);
  }, []);

  const shareUrl = useMemo(() => {
    if (!selectedTrip?._id) return '';
    const baseUrl = process.env.NODE_ENV === 'production' 
      ? 'https://ifinds.in' 
      : window.location.origin.replace('https://', 'http://');
    const tripType = selectedTrip?.type === 'itinerary' ? '?type=itinerary' : '';
    return `${baseUrl}/trips/${selectedTrip._id}${tripType}`;
  }, [selectedTrip]);

  const shareText = useMemo(() => {
    if (!selectedTrip) return '';
    return selectedTrip?.description || `Check out this ${selectedTrip?.type === 'itinerary' ? 'itinerary' : 'trip'}: ${selectedTrip?.title || ''}`;
  }, [selectedTrip]);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography onClick={handleHeaderClick} fontWeight={500} sx={{fontSize: '16px', cursor: 'pointer', color: '#1D1B20'}}>Trips and Itineraries</Typography>
        <ArrowForwardIosIcon sx={{ fontSize: 14, color: '#1D1B20', ml: 1 }} />
      </Box>

      {/* Horizontal list */}
      <Box
        sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
          gap: 2,
          pb: 2,
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          '&::-webkit-scrollbar': {
            display: 'none', // Chrome, Safari and Opera
          },
        }}
      >
        {trips?.map((trip: any) => (
          <TripCard
            key={trip._id}
            trip={trip}
            navigate={navigate}
            onShare={handleShare}
          />
        ))}
      </Box>

      <ShareModal
        open={shareModalOpen}
        onClose={handleShareModalClose}
        title={selectedTrip?.title || 'Trip'}
        shareUrl={shareUrl}
        shareText={shareText}
      />
    </Box>
  );
};


//make this component available to the app
export default DashboardTripScreen;
